/* The page's side of one open mailbox: a Web Worker made from this bundle's
 * own URL, so there is nothing extra to fetch or version (the page's security
 * policy allows workers from this site only, which this is).
 *
 * **One `MailboxReader` per open file.** Closing it terminates the worker, and
 * that is the whole of freeing a mailbox's memory.
 */

let workerUrl = typeof document === "undefined" ? null : import.meta.url;

/** For the node harness, which runs the bundle from a path rather than a URL. */
export function useWorkerUrl(url) {
  workerUrl = url;
}

export class MailboxReader {
  /**
   * @param {File} file
   * @param {{onSummaries, onProgress, onDone, onError}} events
   * @param {Worker} [worker] a stand in for tests
   */
  constructor(file, events, worker) {
    this.file = file;
    this.events = events;
    this.worker = worker ?? new Worker(workerUrl, { type: "module" });
    this.pending = new Map();
    this.nextReq = 1;
    this.search = null; // { generation, onProgress, onDone }
    this.generation = 0;

    this.worker.onmessage = ({ data }) => this.receive(data);
    this.worker.onerror = (event) => events.onError?.(new Error(event.message ?? "the reader stopped"));
    this.worker.postMessage({ type: "open", file });
  }

  receive(data) {
    switch (data.type) {
      case "batch":
        this.events.onSummaries?.(data.items);
        break;
      case "progress":
        this.events.onProgress?.(data);
        break;
      case "done":
        this.events.onDone?.(data);
        break;
      case "searchProgress":
        if (this.search?.generation === data.generation) this.search.onProgress?.(data);
        break;
      case "searchDone":
        if (this.search?.generation === data.generation) {
          const { onDone } = this.search;
          this.search = null;
          onDone?.(data);
        }
        break;
      case "message":
      case "raw":
        this.pending.get(data.req)?.resolve(data);
        this.pending.delete(data.req);
        break;
      case "error":
        if (this.pending.has(data.req)) {
          this.pending.get(data.req).reject(new Error(data.message));
          this.pending.delete(data.req);
        } else {
          this.events.onError?.(new Error(data.message));
        }
        break;
    }
  }

  request(message) {
    const req = this.nextReq++;
    return new Promise((resolve, reject) => {
      this.pending.set(req, { resolve, reject });
      this.worker.postMessage({ ...message, req });
    });
  }

  /** The decoded message, attachments as ArrayBuffers. */
  async message(id) {
    return (await this.request({ type: "get", id })).parsed;
  }

  /** The message's own bytes, envelope removed: an `.eml`. */
  async raw(id) {
    return (await this.request({ type: "raw", id })).bytes;
  }

  /**
   * Searches message text. A newer call replaces an older one, whose
   * callbacks then never fire. An empty query cancels.
   */
  find(query, onProgress, onDone) {
    const generation = ++this.generation;
    this.search = query.trim() ? { generation, onProgress, onDone } : null;
    this.worker.postMessage({ type: "search", query, generation });
  }

  close() {
    this.worker.terminate();
    for (const { reject } of this.pending.values()) reject(new Error("closed"));
    this.pending.clear();
  }
}
